import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiExternalLink, FiCalendar, FiAlertCircle, FiGlobe } = FiIcons;

const NewsSection = ({ reliefWebData, gdacsData }) => {
  if (!reliefWebData?.length && !gdacsData?.length) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="bg-white rounded-xl shadow-lg p-6 border border-blue-100"
    >
      <div className="flex items-center mb-4">
        <div className="p-2 bg-blue-100 rounded-lg mr-3">
          <SafeIcon icon={FiGlobe} className="text-xl text-blue-600" />
        </div>
        <h2 className="text-lg font-bold text-gray-900">Situation Reports</h2>
      </div>

      <div className="space-y-4">
        {/* ReliefWeb Reports */}
        {reliefWebData?.length > 0 && (
          <div>
            <h3 className="font-semibold text-sm text-gray-700 mb-2 flex items-center">
              <SafeIcon icon={FiAlertCircle} className="mr-1 text-orange-600" />
              Humanitarian Reports
            </h3>
            <div className="space-y-2">
              {reliefWebData.slice(0, 3).map((report, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="p-3 bg-orange-50 border border-orange-200 rounded-lg"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm text-gray-900 line-clamp-2">
                        {report.title}
                      </h4>
                      <div className="flex items-center mt-1 text-xs text-gray-600">
                        <SafeIcon icon={FiCalendar} className="mr-1" />
                        <span>{new Date(report.date).toLocaleDateString('en-PH')}</span>
                        {report.source && (
                          <>
                            <span className="mx-2">•</span>
                            <span>{report.source}</span>
                          </>
                        )}
                      </div>
                    </div>
                    {report.url && (
                      <a
                        href={report.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 p-1 text-gray-400 hover:text-orange-600 transition-colors"
                      >
                        <SafeIcon icon={FiExternalLink} className="text-sm" />
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* GDACS Alerts */}
        {gdacsData?.length > 0 && (
          <div>
            <h3 className="font-semibold text-sm text-gray-700 mb-2 flex items-center">
              <SafeIcon icon={FiAlertCircle} className="mr-1 text-red-600" />
              Global Disaster Alerts
            </h3>
            <div className="space-y-2">
              {gdacsData.slice(0, 3).map((alert, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="p-3 bg-red-50 border border-red-200 rounded-lg"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm text-gray-900 line-clamp-2">
                        {alert.title}
                      </h4>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                        {alert.description?.replace(/<[^>]*>/g, '')}
                      </p>
                      <div className="flex items-center mt-1 text-xs text-gray-500">
                        <SafeIcon icon={FiCalendar} className="mr-1" />
                        <span>{new Date(alert.pubDate).toLocaleDateString('en-PH')}</span>
                      </div>
                    </div>
                    {alert.link && (
                      <a
                        href={alert.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 p-1 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <SafeIcon icon={FiExternalLink} className="text-sm" />
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-blue-800 text-xs">
          <strong>Sources:</strong> ReliefWeb provides humanitarian impact reports. 
          GDACS offers global disaster coordination alerts updated every ~6 minutes.
        </p>
      </div>
    </motion.div>
  );
};

export default NewsSection;